import { apiClient as api } from './apiClient';

/**
 * Seguimiento de pactos (Pacto de Firma por WhatsApp). Solo lectura, y solo Sistemas: el backend exige los
 * recursos ACL `Pactos.Seguimiento.Read` y `Pactos.Seguimiento.Detalle.Read` y responde 403 al resto. Los tipos
 * son espejo de los DTOs de `GCvscBE.Application.SeguimientoPactos`.
 *
 * Las fechas llegan con desfase explícito (`DateTimeOffset`), así que se parsean tal cual: no hace falta
 * añadir una `Z` como en FDoc.
 */
export interface PactoEnvioEstado {
  idPactoEnvio: number;
  /** 8 primeros caracteres del identificador del mensaje. Es solo una referencia; nunca llega entero. */
  referencia: string;
  idCanal: number;
  canal: string;
  /** 1 Enviado · 2 Aceptado · 3 Declinado */
  idEstado: number;
  estado: string;
  fechaEnvio: string;
  fechaRespuesta?: string | null;
  minutosHastaRespuesta?: number | null;
  diasSinRespuesta?: number | null;
  idOperacion: number;
  ccl?: string | null;
  aliasOperacion?: string | null;
  /** Código del cliente (CL12345). El listado no lleva nombre. */
  codigoCliente?: string | null;
  idAsesor?: number | null;
  asesor?: string | null;
  pactoFirmado: boolean;
  fechaPacto?: string | null;
  /** Envíos del mismo cliente y operación (1 = sin repetición). */
  enviosMismoPacto: number;
}

export interface PactosPagina<T> {
  total: number;
  pagina: number;
  tamano: number;
  items: T[];
}

export interface PactosResumen {
  enviados: number;
  aceptados: number;
  declinados: number;
  pendientes: number;
  pendientesAntiguos: number;
  umbralDias: number;
  clientesConDobleEnvio: number;
  minutosMediaAceptacion?: number | null;
  minutosMedianaAceptacion?: number | null;
}

export interface PactoCronologiaEnvio {
  idPactoEnvio: number;
  referencia: string;
  canal: string;
  idEstado: number;
  estado: string;
  fechaEnvio: string;
  fechaRespuesta?: string | null;
  minutosHastaRespuesta?: number | null;
  esSeleccionado: boolean;
}

/** Con el nombre del cliente (PII): solo responde 200 con el recurso de detalle. */
export interface PactoCronologia {
  idPactoEnvio: number;
  idOperacion: number;
  ccl?: string | null;
  aliasOperacion?: string | null;
  codigoCliente?: string | null;
  cliente?: string | null;
  idPactoWeb?: number | null;
  fechaPacto?: string | null;
  envios: PactoCronologiaEnvio[];
}

export interface SeguimientoPactosFiltro {
  estado?: number;
  canal?: number;
  /** ISO 8601, inclusive. */
  desde?: string;
  hasta?: string;
  /** CCL o alias de la operación. */
  operacion?: string;
  idAsesor?: number;
  sinRespuestaMasDeDias?: number;
  umbralDias?: number;
  pagina?: number;
  tamano?: number;
}

export const ESTADOS_PACTO: { value: number; label: string }[] = [
  { value: 1, label: 'Enviado' },
  { value: 2, label: 'Aceptado' },
  { value: 3, label: 'Declinado' },
];

export const CANALES_PACTO: { value: number; label: string }[] = [
  { value: 1, label: 'WhatsApp' },
  { value: 2, label: 'SMS' },
];

const buildQuery = (filtro: SeguimientoPactosFiltro): string => {
  const parts = Object.entries(filtro)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export const getEnvios = async (filtro: SeguimientoPactosFiltro = {}): Promise<PactosPagina<PactoEnvioEstado>> => {
  const { data } = await api.get<PactosPagina<PactoEnvioEstado>>(`/api/seguimiento-pactos/envios${buildQuery(filtro)}`);
  return data;
};

/** KPIs del mismo filtro (la paginación no cuenta). */
export const getResumen = async (filtro: SeguimientoPactosFiltro = {}): Promise<PactosResumen> => {
  const { pagina: _pagina, tamano: _tamano, ...sinPaginar } = filtro;
  const { data } = await api.get<PactosResumen>(`/api/seguimiento-pactos/resumen${buildQuery(sinPaginar)}`);
  return data;
};

/** Cronología con nombre de cliente. 403 si el usuario no tiene el recurso de detalle. */
export const getCronologia = async (idPactoEnvio: number): Promise<PactoCronologia> => {
  const { data } = await api.get<PactoCronologia>(`/api/seguimiento-pactos/envios/${idPactoEnvio}/cronologia`);
  return data;
};

/** Minutos → «12 min», «3 h 20 min», «2 d 4 h». */
export const fmtDuracionMinutos = (min?: number | null): string => {
  if (min == null) return '—';
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${Math.round(min % 60)} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
};
